import {
  ICashBreakdown,
  ICashBreakdownResponse,
} from '@modules/front-office/interface/fo-cash-breakdown.interface';
import { api } from '@services/api';
import { setFOBookingAccountsList } from '@store/slices/front-office/fo-booking-accounts-list.slice';
import { setBookingOptionList } from '@store/slices/front-office/fo-booking-option-list.slice';
import { setFOCalendarFilters } from '@store/slices/front-office/fo-calendar-filters.slice';
import { setNationalityList } from '@store/slices/nationality-list.slice';
import moment, { Moment } from 'moment';
import {
  FOAccountMasterlistInterface,
  FOBookingCancelPayloadInterface,
  IFOBookingCheckInPayload,
  FOBookingCheckoutPayloadInterface,
  FOBookingOptionsListAllPropertiesObjectInterface,
  FOBookingOptionsListInterface,
  FOBookingOptionsListNationalityInterface,
  FOBookingOptionsListTransformedInterface,
  FOBookingPayloadInterface,
  FOBookingRoomCodeTransferPayloadInterface,
  FOBookingRoomRatesInterface,
  FOBookingRoomRatesPayloadInterface,
  FOBookingRoomStatusPayloadInterface,
  FOChildRatesInterface,
  FOCreateBlockingsPayloadInterface,
  FOCreateBookingSharerPayloadInterface,
  FODashboardInterface,
  FODeclareNewDayPayloadInterface,
  FODeleteBookingSharerPayloadInterface,
  FOGetBookingInterface,
  FOGetBookingSharerInterface,
  FOGetBookingsInterface,
  FOGetRoomBlockingInterface,
  FOGroupBookingAddRoomPayloadInterface,
  FOGroupBookingInterface,
  FOGroupBookingPayloadInterface,
  FOGroupBookingsContentPayloadInterface,
  FOGroupBookingsInterface,
  FOGroupBookingsRoomsListInterface,
  FOGroupRoomListInterface,
  FOGroupRoomListPayloadInterface,
  FOPartyBookingPayloadInterface,
  FOReactiveBookingPayloadInterface,
  FORoomConfigurationPayloadInterface,
  FORoomRestrictionPayloadInterface,
  FORoomTariffPayloadInterface,
  FOSalesDateInterface,
  FOUpdateGroupBookingPayloadInterface,
  IBookingEngineReports,
  FOBookingsAvailableRatesInterface,
  IFOCheckInBookingResponse,
} from '../../modules/front-office/interface/fo-api-interface';
import {
  IDeclareNewDayList,
  IDeclareNewDayResult,
  IRoomUnblocking,
} from '../../modules/front-office/interface/fo-dnd-interface';
import {
  CalendarOptionsFilters,
  CalendarOptionsInterface,
  FOAllGridsDataInterface,
  FORoomPlanGridsInterface,
  GridType,
} from '../../modules/front-office/interface/fo-grids-api.interface';
import { IPendingList } from '../../modules/front-office/interface/reports.interface';
import { ICheckOutResponse } from '../../modules/front-office/interface/room-posting-interface';
import { store } from '../../store';
import { handleError } from '../../utils/api-error-handling';
import { encryptPayload } from '../../utils/security';
import { setSalesDate, TModuleCode } from '@store/slices/app.slice';

export type BookingsParamsType =
  | 'reservations'
  | 'tentative'
  | 'confirmed'
  | 'in-house'
  | 'pending'
  | 'departed'
  | 'cancelled'
  | 'arrivals'
  | 'departures'
  | 'waitlist'
  | null;

// type RootState = any; // normally inferred from state

export const frontOfficeApi = api
  .enhanceEndpoints({
    addTagTypes: ['BusinessSources', 'MarketSegments'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      // #region Sprint 1
      // SINGLE RESERVATION
      getBookings: builder.query({
        query: ({
          type,
          unAssignedRooms,
          cancellationDate,
          selectedDate,
        }: {
          type: BookingsParamsType;
          unAssignedRooms?: boolean;
          cancellationDate?: string; // YYYY-MM-DD
          selectedDate?: string; // YYYY-MM-DD
          refetch?: string;
        }) => {
          return {
            url: 'bookings',
            params: {
              ...(type && { 'filter[booking_status]': type }),
              ...(typeof unAssignedRooms !== 'undefined' && {
                'filter[unassigned_room]': String(unAssignedRooms),
              }),
              ...(cancellationDate && {
                'filter[cancellation_date]': cancellationDate,
              }),
              ...(selectedDate && { 'filter[selected_date]': selectedDate }),
            },
          };
        },
        providesTags: ['Bookings'],
        forceRefetch() {
          return true;
        },
        keepUnusedDataFor: 0.0001,
        transformResponse: (baseQueryReturnValue: FOGetBookingsInterface) => {
          return baseQueryReturnValue.data.bookings;
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          // need standard json error structure on be api
          return handleError(baseQueryReturnValue);
        },
      }),
      getBooking: builder.query({
        query: (params: string) => `bookings/${params}`,
        providesTags: ['Specific Booking'],
        keepUnusedDataFor: 0,
        forceRefetch({ currentArg, previousArg }) {
          return currentArg !== previousArg;
        },
        transformResponse: (baseQueryReturnValue: FOGetBookingInterface) => {
          return baseQueryReturnValue.data.bookings;
        },
        async onQueryStarted(_arg, { queryFulfilled }) {
          await queryFulfilled;
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      // #region LOCALSTOGE MASTERLIST
      getAllBookingOptionsList: builder.query({
        query: () => 'bookings/properties_options_list',
        providesTags: [
          'Booking Option List',
          'MarketSegments',
          'BusinessSources',
        ],
        forceRefetch() {
          return true;
        },
        keepUnusedDataFor: 0.0001,
        async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
          try {
            const { data: baseQueryReturnValue } = await queryFulfilled;

            const { getState } = store;
            const data = getState();

            if (
              !baseQueryReturnValue ||
              !data ||
              data.properties.selectedProperty.value == 'All Properties'
            ) {
              return;
            }

            const foundProperty:
              | FOBookingOptionsListAllPropertiesObjectInterface
              | undefined = baseQueryReturnValue.data.properties.find(
              ({
                property,
              }: FOBookingOptionsListAllPropertiesObjectInterface) =>
                property.propertyID === data.properties.selectedProperty.value,
            );

            if (!foundProperty) {
              return;
            }

            // Create a mutable copy of the property object
            const property: FOBookingOptionsListAllPropertiesObjectInterface =
              JSON.parse(JSON.stringify(foundProperty)); // Deep copy

            property.property?.marketSegment?.forEach((segment) => {
              segment.market_segment.ms_options?.forEach((option) => {
                option.rates?.sort((a, b) =>
                  a.rate_code.localeCompare(b.rate_code),
                );

                option.business_source?.sort((a, b) =>
                  a.bsource_code.localeCompare(b.bsource_code),
                );
              });
            });

            property.property?.VIPLevels?.sort((a, b) =>
              a.vip_level.localeCompare(b.vip_level),
            );
            property.property?.salutations?.sort((a, b) =>
              a.salutation.localeCompare(b.salutation),
            );
            property.property?.sub_folios?.sort((a, b) =>
              a.sub_folio.localeCompare(b.sub_folio),
            );

            dispatch(setBookingOptionList(property));
          } catch (error) {
            console.error(error);
          }
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      getNationalityList: builder.query({
        query: () => 'bookings/options_list/?filter[list_for]=nationality',
        providesTags: ['Nationality List'],
        forceRefetch() {
          return true;
        },
        keepUnusedDataFor: 0.0001,
        async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
          try {
            const {
              data: baseQueryReturnValue,
            }: {
              data: {
                data: {
                  bookingSelectOptions: Array<{
                    listNationality: FOBookingOptionsListNationalityInterface[];
                  }>;
                };
              };
            } = await queryFulfilled;

            if (!baseQueryReturnValue) return;

            const newNationalityData =
              baseQueryReturnValue.data.bookingSelectOptions[0]?.listNationality?.map(
                (item) => item,
              );

            if (!newNationalityData) return;

            dispatch(setNationalityList(newNationalityData));
          } catch (error) {
            console.error(error);
          }
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      getAccountsMarterlist: builder.query({
        query: () => 'bookings/accounts_masterlist',
        providesTags: ['Account Masterlist'],
        forceRefetch() {
          return true;
        },
        keepUnusedDataFor: 0,
        async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
          try {
            const {
              data: baseQueryReturnValue,
            }: {
              data: FOAccountMasterlistInterface;
            } = await queryFulfilled;

            const { getState } = store;
            const data = getState();

            if (
              !baseQueryReturnValue ||
              !data ||
              data.properties.selectedProperty.value == 'All Properties'
            ) {
              return;
            }

            const accountsMasterlist =
              baseQueryReturnValue.data.properties
                ?.map(({ property }) => property)
                .find(
                  (item) =>
                    item.property_id === data.properties.selectedProperty.value,
                )
                ?.accounts.map(({ account }) => account) || []; // Ensure to return []

            if (!accountsMasterlist) return;

            dispatch(setFOBookingAccountsList(accountsMasterlist));
          } catch (error) {
            console.error(error);
          }
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      getFOSalesDate: builder.query({
        query: ({
          moduleCode,
        }: {
          moduleCode: TModuleCode;
          refetch?: string;
        }) => ({
          url: 'salesDate',
          headers: {
            module: moduleCode,
          },
        }),
        providesTags: ['Sales Date'],
        forceRefetch() {
          return true;
        },
        async onQueryStarted(arg, { queryFulfilled, dispatch }) {
          try {
            const { data } = await queryFulfilled;
            if (!data) return;

            if (arg.moduleCode) {
              dispatch(
                setSalesDate({
                  code: arg.moduleCode,
                  salesDate: moment(data).format('YYYY-MM-DD'),
                }),
              );
            }
          } catch (err) {
            console.error('Sales date fetch failed:', err);
          }
        },
        keepUnusedDataFor: 0.0001,
        transformResponse(baseQueryReturnValue: FOSalesDateInterface) {
          // setStorage(
          //   'current-sales-date',
          //   baseQueryReturnValue.data.currentSalesDate,
          // );

          //return baseQueryReturnValue;
          return baseQueryReturnValue.data.currentSalesDate;
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      // #endregion
      getBookingsOptionsList: builder.query({
        query: () => 'bookings/options_list',
        transformResponse: (
          baseQueryReturnValue: FOBookingOptionsListInterface,
        ): FOBookingOptionsListTransformedInterface => {
          const data = baseQueryReturnValue.data.bookingSelectOptions;
          return data.reduce(
            (acc: FOBookingOptionsListTransformedInterface, cur: any) => {
              const key:
                | 'listMarketSegmentwithBusSource'
                | 'listNationality'
                | 'listVIP'
                | 'listSalutation' = Object.keys(cur)[0] as any;

              acc[key] = cur[key];
              return acc;
            },
            {},
          );
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      createBooking: builder.mutation({
        query: (payload: FOBookingPayloadInterface) => ({
          url: 'bookings',
          method: 'POST',
          body: encryptPayload(payload),
        }),
        transformResponse(baseQueryReturnValue: {
          data: { bookingReferenceID: string };
        }) {
          return baseQueryReturnValue.data.bookingReferenceID;
        },
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              // this will handle auto refetch
              dispatch(
                frontOfficeApi.util.invalidateTags([
                  'Bookings',
                  'Specific Booking',
                ]),
              );
            }
            if (
              data &&
              arg.properties?.[0].property.bookings?.[0].booking?.[0]
                .guestInformation.account_id === 0
            ) {
              dispatch(
                frontOfficeApi.util.invalidateTags(['Account Masterlist']),
              );
            }
          } catch (error) {
            // Handle error without invalidating tags
          }
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      importBookings: builder.mutation({
        query: (payload: FOBookingPayloadInterface) => ({
          url: 'bookings/import',
          method: 'POST',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              // this will handle auto refetch
              dispatch(
                frontOfficeApi.util.invalidateTags([
                  'Bookings',
                  'Specific Booking',
                ]),
              );
            }
            if (
              data &&
              arg.properties?.[0].property.bookings?.[0].booking?.[0]
                .guestInformation.account_id === 0
            ) {
              dispatch(
                frontOfficeApi.util.invalidateTags(['Account Masterlist']),
              );
            }
          } catch (error) {
            // Handle error without invalidating tags
          }
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return baseQueryReturnValue?.data;
        },
      }),
      updateBooking: builder.mutation({
        query: (payload: FOBookingPayloadInterface) => ({
          url: 'bookings',
          method: 'PUT',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              // this will handle auto refetch
              dispatch(
                frontOfficeApi.util.invalidateTags([
                  'Bookings',
                  'CancelledBookings',
                  'Specific Booking',
                ]),
              );
            }
            if (
              data &&
              arg.properties?.[0].property.bookings?.[0].booking?.[0]
                .guestInformation.account_id === 0
            ) {
              dispatch(
                frontOfficeApi.util.invalidateTags(['Account Masterlist']),
              );
            }
          } catch (error) {
            // Handle error without invalidating tags
          }
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      deleteBookingNetLock: builder.mutation({
        query: (payload: string) => ({
          url: `netlock/${payload}/Booking`,
          method: 'DELETE',
        }),
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      postBookingConfirmStatus: builder.mutation({
        query: (params: { refId: string; status: boolean }) => ({
          url: `bookings/${params.refId}/${params.status}`,
          method: 'PUT',
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            // this will handle auto refetch
            dispatch(
              frontOfficeApi.util.invalidateTags([
                'Bookings',
                'Specific Booking',
              ]),
            );
          } catch (error) {
            // Handle error without invalidating tags
          }
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      // #endregion

      // #region Sprint 2
      // GROUP RESERVATION
      getGroupBookings: builder.query({
        query: () => 'groupbookings',
        providesTags: ['Group Bookings'],
        // forceRefetch() {
        //   return true;
        // },
        // keepUnusedDataFor: 0.0001,
        transformResponse: (baseQueryReturnValue: FOGroupBookingsInterface) => {
          return baseQueryReturnValue.data.groupBookings?.map(
            ({ groupBooking }) => groupBooking,
          );
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      getGroupBooking: builder.query({
        query: (params: string | number) => `groupbookings/${params}`,
        providesTags: ['Group Bookings'],
        forceRefetch() {
          return true;
        },
        keepUnusedDataFor: 0.0001,
        transformResponse: (baseQueryReturnValue: FOGroupBookingInterface) => {
          return baseQueryReturnValue.data.groupBookings?.map(
            ({ groupBooking }) => groupBooking,
          )[0];
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      // MASTERLIST
      getRoomsList: builder.query({
        query: ({
          referenceID,
          checkInDate,
          checkOutDate,
          isCheckIn,
          pax,
        }: {
          referenceID: string;
          isCheckIn: boolean;
          checkInDate: string;
          checkOutDate: string;
          pax: number;
        }) => ({
          url: `bookings/list-available-rooms/${referenceID}/${isCheckIn}/${checkInDate}/${checkOutDate}/${pax}`,
        }),
        transformResponse(
          baseQueryReturnValue: FOGroupBookingsRoomsListInterface,
        ) {
          // return baseQueryReturnValue.data.listRoomTypes[0]?.availableRoomTypes;
          const availableRoomTypes =
            baseQueryReturnValue.data.listRoomTypes[0]?.availableRoomTypes;

          // Sort availableRoomTypes by room_code
          const sortedAvailableRoomTypes = availableRoomTypes?.sort((a, b) =>
            a.room_code.localeCompare(b.room_code),
          );

          // Sort each list_rates by rate_code
          sortedAvailableRoomTypes?.forEach((roomType) => {
            // roomType.list_rates.sort((a, b) =>
            //   a.rate_code.localeCompare(b.rate_code),
            // );
            roomType.list_rooms.sort();
          });

          return sortedAvailableRoomTypes;
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),

      // MASTERLIST
      getRoomsAvailableList: builder.query({
        query: ({
          referenceID,
          isCheckIn,
          checkInDate,
          checkOutDate,
          pax,
        }: {
          referenceID: string;
          isCheckIn: boolean;
          checkInDate: string;
          checkOutDate: string;
          pax: number;
        }) => {
          return {
            url: `bookings/list-available-rooms/${referenceID}/${isCheckIn}/${checkInDate}/${checkOutDate}/${pax}`,
          };
        },
        transformResponse(
          baseQueryReturnValue: FOGroupBookingsRoomsListInterface,
        ) {
          // return baseQueryReturnValue.data.listRoomTypes[0]?.availableRoomTypes;
          const availableRoomTypes =
            baseQueryReturnValue?.data?.listRoomTypes?.[0]
              ?.availableRoomTypes || [];

          return availableRoomTypes
            .map((roomType) => ({
              ...roomType,
              // list_rates: [...(roomType.list_rates || [])].sort((a, b) =>
              //   a.rate_code.localeCompare(b.rate_code),
              // ),
              list_rooms: [...(roomType.list_rooms || [])].sort(),
            }))
            .sort((a, b) => a.room_code.localeCompare(b.room_code));
        },
        transformErrorResponse: async (
          baseQueryReturnValue: FOGroupBookingsRoomsListInterface,
        ) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      getRatesAvailableList: builder.query({
        query: ({
          marketSegment,
          checkInDate,
          checkOutDate,
          pax,
          roomCode,
        }: {
          marketSegment: string;
          checkInDate: string;
          checkOutDate: string;
          pax: number;
          roomCode: string;
        }) => {
          return {
            url: `bookings/list-available-rates/${marketSegment}/${checkInDate}/${checkOutDate}/${pax}/${roomCode}`,
          };
        },
        transformResponse(
          baseQueryReturnValue: FOBookingsAvailableRatesInterface,
        ) {
          const availableRates = baseQueryReturnValue?.data?.listRates || [];

          return availableRates.map((rate) => ({
            ...rate,
            rate_id: rate.rateID,
            rate_code: rate.rateCode,
            rate_default: rate.rateDefault,
            rate_order: rate.rateOrder,
            pax_max: rate.paxMax,
          }));
        },
        transformErrorResponse: async (
          baseQueryReturnValue: FOBookingsAvailableRatesInterface,
        ) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      // END MASTERLIST
      createGroupBooking: builder.mutation({
        query: (payload: FOGroupBookingPayloadInterface) => ({
          url: `groupbookings`,
          method: 'POST',
          body: encryptPayload(payload),
        }),
        invalidatesTags: ['Group Bookings'],
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      updateGroupBooking: builder.mutation({
        query: (payload: FOUpdateGroupBookingPayloadInterface) => ({
          url: `groupbookings`,
          method: 'PUT',
          body: encryptPayload(payload),
        }),
        invalidatesTags: ['Group Bookings'],
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      groupBookingAddRooms: builder.mutation({
        query: (payload: FOGroupBookingAddRoomPayloadInterface) => ({
          url: `groupbookings/addrooms`,
          method: 'POST',
          body: encryptPayload(payload),
        }),
        invalidatesTags: ['Group Bookings'],
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      // #endregion

      // #region Sprint 3
      // PARTY BOOKING
      createPartyBooking: builder.mutation({
        query: (payload: FOPartyBookingPayloadInterface) => ({
          url: 'partybookings/addrooms',
          method: 'POST',
          body: encryptPayload(payload),
        }),
        invalidatesTags: ['Bookings'],
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      // CANCELLED RESETVATION
      getCancelledBookings: builder.query({
        query: () =>
          'bookings/?filter[booking_status]=cancelled&filter[unassigned_room]=false',
        providesTags: ['CancelledBookings'],
        forceRefetch() {
          return true;
        },
        keepUnusedDataFor: 0.0001,
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      // PENDING RESETVATION
      getPendingBookings: builder.query({
        query: () =>
          'bookings/?filter[booking_status]=pending&filter[unassigned_room]=false',
        providesTags: ['Pending Bookings'],
        forceRefetch() {
          return true;
        },
        keepUnusedDataFor: 0.0001,
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
        transformResponse: async (result: IPendingList) => {
          return result;
        },
      }),
      reactivateBooking: builder.mutation({
        query: (payload: FOReactiveBookingPayloadInterface) => ({
          url: 'bookings/reactivate',
          method: 'POST',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            // this will handle auto refetch
            dispatch(
              frontOfficeApi.util.invalidateTags([
                'Bookings',
                'CancelledBookings',
              ]),
            );
          } catch (error) {
            // Handle error without invalidating tags
          }
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      // GRIDS
      getGridData: builder.query({
        query: ({
          gridType,
          numDays,
          rateCode,
          roomType,
          startDate,
        }: {
          startDate?: Moment | null;
          numDays?: string | number;
          gridType?: GridType;
          roomType?: string;
          rateCode?: string;
        }) =>
          `grids?${
            startDate
              ? `filter[startDate]=${moment(startDate).format('YYYY-MM-DD')}`
              : ''
          }${numDays ? `&filter[numDays]=${numDays}` : ''}${
            gridType ? `&filter[gridType]=${gridType}` : ''
          }${roomType ? `&filter[roomType]=${roomType}` : ''}${
            rateCode ? `&filter[rateCode]=${rateCode}` : ''
          }`,
        providesTags: ['Bookings'],
        forceRefetch() {
          return true;
        },
        keepUnusedDataFor: 0.0001,
        transformResponse(baseQueryReturnValue: FOAllGridsDataInterface) {
          return baseQueryReturnValue.data;
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      getRoomPlanData: builder.query({
        query: ({
          numDays,
          rateCode,
          roomType,
          startDate,
          bookingStatus,
          building,
          floor,
        }: {
          startDate?: Moment | null;
          numDays?: string | number;
          roomType?: string;
          rateCode?: string;
          bookingStatus?: string;
          building?: string;
          floor?: string;
        }) =>
          `grids?${
            startDate
              ? `filter[startDate]=${moment(startDate).format('YYYY-MM-DD')}`
              : ''
          }${
            numDays ? `&filter[numDays]=${numDays}` : ''
          }&filter[gridType]=Room Plan${
            roomType ? `&filter[roomType]=${roomType}` : ''
          }${rateCode ? `&filter[rateCode]=${rateCode}` : ''}${
            bookingStatus ? `&filter[bookingStatus]=${bookingStatus}` : ''
          }${building ? `&filter[building]=${building}` : ''}${
            floor ? `&filter[floor]=${floor}` : ''
          }`,

        providesTags: ['Bookings'],
        forceRefetch() {
          return true;
        },
        keepUnusedDataFor: 0,
        transformResponse(baseQueryReturnValue: FORoomPlanGridsInterface) {
          return baseQueryReturnValue.data;
        },
        async onQueryStarted(_arg, { queryFulfilled }) {
          await queryFulfilled;
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      getCalendarOptions: builder.query({
        query: () => 'lists/fo-calendar-filters',
        forceRefetch() {
          return true;
        },
        keepUnusedDataFor: 0.0001,
        async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
          try {
            const {
              data: baseQueryReturnValue,
            }: { data: CalendarOptionsInterface } = await queryFulfilled;

            const { getState } = store;
            const data = getState();

            if (
              !baseQueryReturnValue ||
              !data ||
              data.properties.selectedProperty.value == 'All Properties'
            ) {
              return;
            }

            const calendarFiltersInitial = baseQueryReturnValue.data.filters;

            if (!calendarFiltersInitial) return;

            const calendarFilters: CalendarOptionsFilters = JSON.parse(
              JSON.stringify(calendarFiltersInitial),
            );
            calendarFilters?.bookingStatuses?.sort((a, b) =>
              a.bookingStatus.status.localeCompare(b.bookingStatus.status),
            );
            calendarFilters?.gridTypes?.sort(
              (a, b) => a.gridType.order - b.gridType.order,
            );
            calendarFilters?.rates?.sort((a, b) =>
              a.rate.code.localeCompare(b.rate.code),
            );
            calendarFilters?.rooms?.sort((a, b) =>
              a.roomType.code.localeCompare(b.roomType.code),
            );

            dispatch(setFOCalendarFilters(calendarFilters));
          } catch (error) {
            console.error(error);
          }
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      // GRIDS INTEGRATIONS
      postRoomConfigurations: builder.mutation({
        query: (payload: FORoomConfigurationPayloadInterface) => ({
          url: '/integrations/channels/room-configurations',
          method: 'POST',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              dispatch(api.util.invalidateTags(['Bookings']));
            }
          } catch (error) {
            // Handle error without invalidating tags
          }
        },
        transformErrorResponse: async (baseQueryReturnValue) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      postRoomRestriction: builder.mutation({
        query: (payload: FORoomRestrictionPayloadInterface) => ({
          url: '/integrations/channels/restrictions',
          method: 'POST',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              dispatch(api.util.invalidateTags(['Bookings']));
            }
          } catch (error) {
            // Handle error without invalidating tags
          }
        },
        transformErrorResponse: async (baseQueryReturnValue) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      // CHECKOUT
      postCheckOut: builder.mutation({
        query: (payload: FOBookingCheckoutPayloadInterface) => ({
          url: 'bookings/checkout',
          method: 'POST',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            const statusCode = data?.data?.checkOutResponse?.[0]?.code;
            if (statusCode >= 200 && statusCode < 300) {
              // Run this code for successful 2xx status codes
              dispatch(
                frontOfficeApi.util.invalidateTags([
                  'Bookings',
                  'Specific Booking',
                  'Pending Bookings',
                  'DeclareNewDay',
                ]),
              );
            }
          } catch (error) {
            // Handle error without invalidating tags
          }
        },
        transformErrorResponse: async (baseQueryReturnValue) => {
          return handleError(baseQueryReturnValue);
        },
        transformResponse: async (response: ICheckOutResponse) => {
          return response;
        },
      }),
      // #endregion

      // #region Sprint 4
      // CHECKIN
      checkInBooking: builder.mutation<
        IFOCheckInBookingResponse,
        IFOBookingCheckInPayload
      >({
        query: (payload) => ({
          url: 'bookings/checkin',
          method: 'PUT',
          body: encryptPayload(payload),
        }),
        invalidatesTags: ['Bookings'],
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data: response } = await queryFulfilled;
            if (response?.status === 200) {
              dispatch(
                frontOfficeApi.util.invalidateTags([
                  'Bookings',
                  'Specific Booking',
                  'DeclareNewDay',
                ]),
              );
            }
          } catch (error) {
            // Handle error without invalidating tags
          }
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      // CANCEL BOOKING
      cancelBooking: builder.mutation({
        query: (payload: FOBookingCancelPayloadInterface) => ({
          url: 'bookings',
          method: 'DELETE',
          body: encryptPayload(payload),
        }),
        invalidatesTags: ['Bookings'],
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      // #endregion

      // #region Sprint 5
      getFODashboardData: builder.query({
        query: () => 'dashboards/frontOffice',
        providesTags: ['Bookings'],
        forceRefetch() {
          return true;
        },
        keepUnusedDataFor: 0.0001,

        transformResponse(baseQueryReturnValue: FODashboardInterface) {
          return baseQueryReturnValue.data;
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),

      getFODashboardDataVIP: builder.query({
        query: (params: string) =>
          // `declare-new-day/frontOffice?filter[showVIP]=${params}`,
          `dashboards/frontOffice?filter[showVIP]=${params}`,
        providesTags: ['Bookings'],
        forceRefetch() {
          return true;
        },
        keepUnusedDataFor: 0.0001,
        transformResponse(baseQueryReturnValue: FODashboardInterface) {
          return baseQueryReturnValue.data;
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),

      getBookingRoomRates: builder.query({
        query: (params: string) => `/bookings-room-rates/${params}`,
        providesTags: ['Booking Room Rates'],
        forceRefetch() {
          return true;
        },
        keepUnusedDataFor: 0.0001,
        transformResponse(baseQueryReturnValue: FOBookingRoomRatesInterface) {
          return baseQueryReturnValue.data;
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      getGroupRoomList: builder.mutation({
        query: (payload: FOGroupRoomListPayloadInterface) => ({
          url: `groupbookings/roominglist`,
          method: 'POST',
          body: encryptPayload(payload),
        }),
        transformResponse(baseQueryReturnValue: FOGroupRoomListInterface) {
          return baseQueryReturnValue.data.listGroupRoomList;
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      sendEmailVoucher: builder.mutation({
        query: (payload: { file: Blob; data: any }) => {
          const formData = new FormData();
          const voucherName =
            payload?.data?.data[0]?.bookingData?.voucher_name ?? 'sample';
          formData.append('file', payload.file, `${voucherName}.pdf`);
          // Append additional data to the FormData object
          if (payload.data && Object.keys(payload.data).length > 0) {
            for (const key in payload.data) {
              if (payload.data.hasOwnProperty(key)) {
                formData.append(key, JSON.stringify(payload.data[key])); // Serialize the object
              }
            }
          }
          return {
            url: 'bookings/sendEmailVoucher',
            method: 'POST',
            body: encryptPayload(formData),
          };
        },
        transformErrorResponse: async (baseQueryReturnValue) => {
          return handleError(baseQueryReturnValue);
        },
        transformResponse: async (response: ICheckOutResponse) => {
          return response;
        },
      }),
      sendGroupEmailVoucher: builder.mutation({
        query: (payload: { file: Blob; data: any }) => {
          const formData = new FormData();
          const voucherName =
            payload?.data?.data[0]?.bookingData?.voucher_name ?? 'sample';
          formData.append('file', payload.file, `${voucherName}.pdf`);
          // Append additional data to the FormData object
          if (payload.data && Object.keys(payload.data).length > 0) {
            for (const key in payload.data) {
              if (payload.data.hasOwnProperty(key)) {
                formData.append(key, JSON.stringify(payload.data[key])); // Serialize the object
              }
            }
          }
          return {
            url: 'bookings/sendGroupEmailVoucher',
            method: 'POST',
            body: encryptPayload(formData),
          };
        },
        transformErrorResponse: async (baseQueryReturnValue) => {
          return handleError(baseQueryReturnValue);
        },
        transformResponse: async (response: ICheckOutResponse) => {
          return response;
        },
      }),
      availableChildRates: builder.mutation({
        query: (payload: {
          properties: {
            property: {
              propertyID: string;
              filters: {
                rateCode: string;
                roomCode: string;
                dateStart: string;
                dateEnd: string;
                ms: string;
                pax: string;
              }[];
            };
          }[];
        }) => ({
          url: 'bookings-room-rates/childrates',
          method: 'POST',
          body: encryptPayload(payload),
        }),
        transformResponse(baseQueryReturnValue: FOChildRatesInterface) {
          return baseQueryReturnValue.data.listChildRates;
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      updatedBookingRoomRates: builder.mutation({
        query: (payload: FOBookingRoomRatesPayloadInterface) => ({
          url: 'bookings-room-rates',
          method: 'PUT',
          body: encryptPayload(payload),
        }),
        transformResponse(baseQueryReturnValue: {
          data: { bookingReferenceID: string };
        }) {
          return baseQueryReturnValue.data.bookingReferenceID;
        },
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              // this will handle auto refetch
              dispatch(
                frontOfficeApi.util.invalidateTags([
                  'Booking Room Rates',
                  'Specific Booking',
                ]),
              );
            }
          } catch (error) {
            // Handle error without invalidating tags
          }
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      updateRoomStatus: builder.mutation({
        query: (payload: FOBookingRoomStatusPayloadInterface) => ({
          url: 'declare-new-day/rooms-status',
          method: 'PUT',
          body: encryptPayload(payload),
        }),
        invalidatesTags: ['DeclareNewDay', 'Bookings'],
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      updateRoomCodeTransfer: builder.mutation({
        query: (payload: FOBookingRoomCodeTransferPayloadInterface) => ({
          url: 'roomcode-transfer',
          method: 'PUT',
          body: encryptPayload(payload),
        }),
        invalidatesTags: ['Bookings'],
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      updateGroupBookingsContent: builder.mutation({
        query: (payload: FOGroupBookingsContentPayloadInterface) => ({
          url: 'groupbookings/contents',
          method: 'PUT',
          body: encryptPayload(payload),
        }),
        // invalidatesTags: ['Group Bookings'],
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      putUndoCheckOut: builder.mutation({
        query: ({
          bookingRefId,
          bookingStatus,
        }: {
          bookingRefId: string;
          bookingStatus: BookingsParamsType;
        }) => ({
          url: `bookings/${bookingRefId}/check-out/undo?bookingStatus=${bookingStatus}`,
          method: 'PUT',
        }),
        // invalidatesTags: ['Group Bookings'],
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              // this will handle auto refetch
              dispatch(
                frontOfficeApi.util.invalidateTags([
                  'Bookings',
                  'Specific Booking',
                  'DeclareNewDay',
                ]),
              );
            }
          } catch (error) {
            // Handle error without invalidating tags
          }
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      putUndoCheckIn: builder.mutation({
        query: ({ bookingRefId, roomStatus }) => ({
          url: `bookings/${bookingRefId}/check_in/undo?postRoomStatus=${roomStatus}`,
          method: 'PUT',
        }),

        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              dispatch(frontOfficeApi.util.invalidateTags(['Bookings']));
            }
          } catch (error) {
            // Handle error without invalidating tags
          }
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      // #endregion

      // #region Declare New Day

      getNightAuditList: builder.query({
        query: (params: string) => `declare-new-day${params}`,
        providesTags: ['DeclareNewDay'],
        forceRefetch() {
          return true;
        },
        keepUnusedDataFor: 0.0001,
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
        transformResponse: (response: IDeclareNewDayList) => {
          return response;
        },
      }),
      postDeclareNewDay: builder.mutation({
        query: (payload: FODeclareNewDayPayloadInterface) => ({
          url: 'declare-new-day',
          method: 'POST',
          body: encryptPayload(payload),
        }),
        invalidatesTags: ['DeclareNewDay', 'Bookings'],
        transformErrorResponse: async (baseQueryReturnValue) => {
          return handleError(baseQueryReturnValue);
        },
        transformResponse: async (response: IDeclareNewDayResult) => {
          return response;
        },
      }),
      postRoomTariff: builder.mutation({
        query: (payload: FORoomTariffPayloadInterface) => ({
          url: 'declare-new-day/room-tariff',
          method: 'POST',
          body: encryptPayload(payload),
        }),
        //invalidatesTags: ['DeclareNewDay', 'Bookings'],
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              // this will handle auto refetch
              dispatch(frontOfficeApi.util.invalidateTags(['DeclareNewDay']));
            }
          } catch (error) {
            // Handle error without invalidating tags
          }
        },
        transformErrorResponse: async (baseQueryReturnValue) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      deleteSessions: builder.mutation({
        query: ({
          payload,
          propertyID,
        }: {
          payload: string;
          propertyID?: string;
        }) => ({
          url: `declare-new-day/sessions/${payload}`,
          method: 'DELETE',
          headers: propertyID ? { propertyID } : undefined,
        }),
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      // #endregion

      // #region SPRINT 6
      getRoomBlockings: builder.query({
        query: (params?: string) => {
          if (!params) return `blockings`;
          return `blockings/${params}`;
        },
        providesTags: ['Room Blockings'],
        forceRefetch() {
          return true;
        },
        keepUnusedDataFor: 0.0001,
        transformResponse: (
          baseQueryReturnValue: FOGetRoomBlockingInterface,
        ) => {
          return baseQueryReturnValue.data.roomBlockings
            ?.reduce<FOGetRoomBlockingInterface['data']['roomBlockings']>(
              (acc, current) => {
                const { blockingID } = current.roomBlocking;
                if (
                  !acc.some(
                    (item) => item.roomBlocking.blockingID === blockingID,
                  )
                ) {
                  acc.push(current);
                }
                return acc;
              },
              [],
            )
            ?.map((item) => item.roomBlocking);
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      postRoomBlocking: builder.mutation({
        query: (payload: FOCreateBlockingsPayloadInterface) => ({
          url: 'blockings',
          method: 'POST',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              // this will handle auto refetch
              dispatch(
                frontOfficeApi.util.invalidateTags([
                  'Room Blockings',
                  'Bookings',
                ]),
              );
            }
          } catch (error) {
            // Handle error without invalidating tags
          }
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          // need standard json error structure on be api
          return handleError(baseQueryReturnValue);
        },
      }),
      postUnblockRoomBlocking: builder.mutation({
        query: (param: string) => ({
          url: `blockings/${param}/unblock`,
          method: 'POST',
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              dispatch(
                frontOfficeApi.util.invalidateTags([
                  'Room Blockings',
                  'Bookings',
                ]),
              );
            }
          } catch (error) {
            // Handle error without invalidating tags
          }
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      putRoomBlocking: builder.mutation({
        query: (params: {
          id: string;
          payload: FOCreateBlockingsPayloadInterface;
        }) => ({
          url: `blockings/${params.id}`,
          method: 'PUT',
          body: encryptPayload(params.payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              dispatch(
                frontOfficeApi.util.invalidateTags([
                  'Room Blockings',
                  'Bookings',
                ]),
              );
            }
          } catch (error) {
            // Handle error without invalidating tags
          }
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      postUnblockRoomBlockingMultiple: builder.mutation({
        query: (payload: IRoomUnblocking) => ({
          url: `blockings/multi-unblock`,
          method: 'POST',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              dispatch(
                frontOfficeApi.util.invalidateTags([
                  'Room Blockings',
                  'Bookings',
                  'DeclareNewDay',
                ]),
              );
            }
          } catch (error) {
            // Handle error without invalidating tags
          }
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      getBookingSharer: builder.query({
        query: (params: string) => {
          return `bookings/${params}/sharers`;
        },
        providesTags: ['Booking Sharer'],
        forceRefetch() {
          return true;
        },
        keepUnusedDataFor: 0.0001,
        transformResponse: (
          baseQueryReturnValue: FOGetBookingSharerInterface,
        ) => {
          return baseQueryReturnValue.data.sharers?.map((item) => item.sharer);
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      postBookingSharer: builder.mutation({
        query: (params: {
          id: string;
          payload: FOCreateBookingSharerPayloadInterface;
        }) => ({
          url: `bookings/${params.id}/sharers`,
          method: 'POST',
          body: encryptPayload(params.payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              dispatch(frontOfficeApi.util.invalidateTags(['Booking Sharer']));
            }
          } catch (error) {
            // Handle error without invalidating tags
          }
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      deleteBookingSharer: builder.mutation({
        query: (params: {
          id: string;
          payload: FODeleteBookingSharerPayloadInterface;
        }) => ({
          url: `bookings/${params.id}/sharers`,
          method: 'DELETE',
          body: encryptPayload(params.payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              dispatch(frontOfficeApi.util.invalidateTags(['Booking Sharer']));
            }
          } catch (error) {
            // Handle error without invalidating tags
          }
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      // #endregion

      getCashBreakdown: builder.query({
        query: (params: string) => {
          return `cash-breakdown/${params}`;
        },
        providesTags: ['Cash Breakdown'],
        forceRefetch() {
          return true;
        },
        keepUnusedDataFor: 0,
        transformResponse: (baseQueryReturnValue: ICashBreakdownResponse) => {
          return baseQueryReturnValue?.data?.cashBreakdownList?.[0]?.[0];
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
      postCashBreakdown: builder.mutation({
        query: (payload: ICashBreakdown) => {
          return {
            url: '/cash-breakdown',
            method: 'POST',
            body: encryptPayload(payload),
          };
        },
        transformResponse: (baseQueryReturnValue: any) => {
          return baseQueryReturnValue?.data?.cashBreakdownReferenceID ?? '';
        },
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              dispatch(frontOfficeApi.util.invalidateTags(['Cash Breakdown']));
            }
          } catch (error) {
            // Handle error without invalidating tags
          }
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),

      getBookingEngineReports: builder.query({
        query: (params: IBookingEngineReports) => {
          const {
            platform,
            businessSource,
            email,
            bookingStatus,
            startDate,
            endDate,
            reference,
            dateType,
          } = params;

          const queryParams = [
            platform && `filter[platform]=${platform}`,
            businessSource && `filter[bussinessSource]=${businessSource}`,
            email && `filter[email]=${email}`,
            bookingStatus && `filter[bookingStatus]=${bookingStatus}`,
            startDate && `filter[bookingStartDate]=${startDate}`,
            endDate && `filter[bookingEndDate]=${endDate}`,
            reference && `filter[reference]=${reference}`,
            dateType && `filter[dateType]=${dateType}`,
          ]
            .filter(Boolean)
            .join('&');

          return `reports/external-booking?${queryParams}`;
        },

        providesTags: (_result, _error, params) => [
          'Bookings',
          {
            type: 'Bookings' as const,
            id: `booking-engine-${params.startDate}-${params.endDate}-${params.bookingStatus || 'all'}`,
          },
        ],
        keepUnusedDataFor: 300, // Keep data for 5 minutes
        forceRefetch({ currentArg, previousArg }) {
          if (!previousArg) return true;

          const keyParams = [
            'startDate',
            'endDate',
            'bookingStatus',
            'reference',
            'dateType',
          ];
          return keyParams.some(
            (key) => currentArg?.[key] !== previousArg?.[key],
          );
        },
        transformResponse: (baseQueryReturnValue: any) => {
          if (!baseQueryReturnValue?.data?.bookings) {
            return { data: { bookings: [] } };
          }
          return baseQueryReturnValue;
        },
        transformErrorResponse: async (baseQueryReturnValue: any) => {
          return handleError(baseQueryReturnValue);
        },
      }),
    }),
  });

export const {
  // SINGLE RESERVATIONS
  useGetBookingsQuery,
  useCreateBookingMutation,
  useGetBookingsOptionsListQuery,
  useLazyGetBookingQuery,
  useGetBookingQuery,
  useUpdateBookingMutation,
  useDeleteBookingNetLockMutation,
  useLazyGetAllBookingOptionsListQuery,
  useGetAllBookingOptionsListQuery,
  useGetNationalityListQuery,
  useCancelBookingMutation,
  usePostBookingConfirmStatusMutation,

  // GROUP RESERVATIONS
  useGetGroupBookingsQuery,
  useLazyGetGroupBookingQuery,
  useGetRoomsAvailableListQuery,
  useGetRatesAvailableListQuery,
  useGetRoomsListQuery,
  useLazyGetRoomsListQuery,
  useGetAccountsMarterlistQuery,
  useCreateGroupBookingMutation,
  useGroupBookingAddRoomsMutation,
  useUpdateGroupBookingMutation,

  // PARTY BOOKING
  useCreatePartyBookingMutation,

  // CANCELLED RESERVATIONS
  useGetCancelledBookingsQuery,

  // PENDING RESERVATIONS
  useGetPendingBookingsQuery,

  // REACTIVE RESERVATIONS
  useReactivateBookingMutation,

  // GRIDS
  useGetGridDataQuery,
  useLazyGetGridDataQuery,
  useGetRoomPlanDataQuery,
  useLazyGetRoomPlanDataQuery,
  useGetCalendarOptionsQuery,

  // GRIDS INTEGRATION
  usePostRoomConfigurationsMutation,
  usePostRoomRestrictionMutation,

  // CHECKOUT
  usePostCheckOutMutation,

  // CHECKIN BOOKING
  useCheckInBookingMutation,

  // DASHBOARD
  useGetFODashboardDataQuery,
  useGetFODashboardDataVIPQuery,

  // SPRINT 5
  useGetBookingRoomRatesQuery,
  useGetFOSalesDateQuery,
  useAvailableChildRatesMutation,
  useUpdatedBookingRoomRatesMutation,
  useUpdateRoomCodeTransferMutation,
  useGetGroupRoomListMutation,
  useUpdateGroupBookingsContentMutation,

  //DECLARE NEW DAY
  useLazyGetNightAuditListQuery,
  useGetNightAuditListQuery,
  useUpdateRoomStatusMutation,
  usePostDeclareNewDayMutation,
  usePostRoomTariffMutation,
  useDeleteSessionsMutation,

  // SEND EMAIL
  useSendEmailVoucherMutation,
  useSendGroupEmailVoucherMutation,

  usePutUndoCheckOutMutation,
  usePutUndoCheckInMutation,

  // ROOM BLOCKINGS
  useGetRoomBlockingsQuery,
  usePostRoomBlockingMutation,
  usePostUnblockRoomBlockingMutation,
  usePutRoomBlockingMutation,
  usePostUnblockRoomBlockingMultipleMutation,

  // BOOKING SHARER
  useGetBookingSharerQuery,
  usePostBookingSharerMutation,
  useDeleteBookingSharerMutation,

  // IMPORT BOOKINGS
  useImportBookingsMutation,

  // CASH BREAKDOWN
  useGetCashBreakdownQuery,
  useLazyGetCashBreakdownQuery,
  usePostCashBreakdownMutation,

  // BOOKING ENGINE
  useLazyGetBookingEngineReportsQuery,
} = frontOfficeApi;
